import { create } from '@bufbuild/protobuf';
import { Code, ConnectError } from '@connectrpc/connect';
import {
  CommitBranchChangeSchema,
  CommitFilesToBranchMetadataSchema,
  CommitFilesToBranchRequestSchema,
  RepositoryEntryKind,
  type ChangeType,
  type CommitInfo,
  type CommitFilesToBranchRequest,
} from '@puppycad/proto';
import { logger } from '@/utils/logger';

/**
 * 默认文件提交分片大小。
 */
export const DEFAULT_COMMIT_CHUNK_SIZE = 1024 * 1024;

/**
 * `CommitFilesToBranch` 客户端流调用选项。
 */
export interface CommitFilesToBranchInvokeOptions {
  /** 调用方持有的取消信号，用于中断 Connect 流式 RPC。 */
  readonly signal?: AbortSignal;
}

/**
 * `CommitFilesToBranch` 客户端流调用函数。
 */
export type CommitFilesToBranchInvoker = (
  stream: AsyncIterable<CommitFilesToBranchRequest>,
  options?: CommitFilesToBranchInvokeOptions,
) => Promise<CommitInfo>;

/**
 * 单个文件提交载荷。
 */
export interface CommitFilesToBranchFilePayload {
  /** 仓库内相对路径。 */
  readonly path: string;
  /** 文件业务 UUID；新增文件或后端按路径解析时允许为空。 */
  readonly fileUuid: string;
  /** 文件变更类型。 */
  readonly changeType: ChangeType;
  /** 文件内容字节。 */
  readonly bytes: Uint8Array;
}

/**
 * 创建 `CommitFilesToBranch` 客户端请求流所需的输入。
 */
export interface CreateCommitFilesToBranchRequestStreamInput {
  /** 目标仓库 ID。 */
  readonly repositoryId: bigint;
  /** 目标分支 ID。 */
  readonly branchId: bigint;
  /** 提交说明。 */
  readonly message: string;
  /** 本次提交基于的父提交 ID。 */
  readonly baseCommitId: string;
  /** 本次提交包含的文件变更。 */
  readonly files: readonly CommitFilesToBranchFilePayload[];
  /** 每个 chunk 的最大字节数。 */
  readonly chunkSize?: number;
  /** 调用方持有的取消信号，用于停止后续请求帧生成。 */
  readonly signal?: AbortSignal;
}

/**
 * 提交文件日志摘要。
 */
interface CommitFilesToBranchFileLogSummary {
  /** 文件在本次提交中的顺序。 */
  readonly index: number;
  /** 仓库内相对路径。 */
  readonly path: string;
  /** 文件业务 UUID。 */
  readonly fileUuid: string;
  /** 文件变更类型。 */
  readonly changeType: ChangeType;
  /** 文件内容字节数。 */
  readonly byteLength: number;
}

/**
 * 打印本次版本提交的文件摘要。
 *
 * 设计说明：这里只打印文件元信息，不打印 bytes 内容，避免 `.puppy` 二进制或大型 JSON
 * 污染控制台，同时仍能确认提交中是否只包含预期文件。
 *
 * @param input 提交流输入。
 * @param baseCommitId 已归一化的 base commit。
 */
function logCommitFilesToBranchSummary(
  input: CreateCommitFilesToBranchRequestStreamInput,
  baseCommitId: string,
): void {
  const files: CommitFilesToBranchFileLogSummary[] = input.files.map((file, index) => ({
    index,
    path: file.path,
    fileUuid: file.fileUuid,
    changeType: file.changeType,
    byteLength: file.bytes.byteLength,
  }));

  logger.info('[CommitFilesToBranch] 提交文件列表', {
    repositoryId: input.repositoryId.toString(),
    branchId: input.branchId.toString(),
    baseCommitId,
    fileCount: files.length,
    files,
  });
}

/**
 * 按固定大小切分字节数组。
 *
 * @param bytes 完整文件字节。
 * @param chunkSize 每个 chunk 的最大字节数。
 * @returns 按顺序生成的 chunk。
 */
function* createByteChunks(bytes: Uint8Array, chunkSize: number): Iterable<Uint8Array> {
  if (chunkSize <= 0) {
    throw new Error(`提交 chunkSize 必须大于 0，当前值：${chunkSize}`);
  }

  for (let offset = 0; offset < bytes.byteLength; offset += chunkSize) {
    yield bytes.subarray(offset, Math.min(offset + chunkSize, bytes.byteLength));
  }
}

/**
 * 校验文件提交流取消信号是否仍然有效。
 *
 * 设计说明：客户端流在 metadata/change/chunk 之间可能被页面卸载或新请求取消。
 * 这里主动抛 Connect 取消错误，让上层能够按统一取消分支静默收口，而不是误报提交失败。
 *
 * @param signal 调用方传入的取消信号。
 */
function assertCommitFilesToBranchSignalActive(signal: AbortSignal | undefined): void {
  if (signal?.aborted) {
    throw new ConnectError('文件提交请求已取消', Code.Canceled);
  }
}

/**
 * 校验提交流基础上下文。
 *
 * @param input 提交流输入。
 * @returns 去除空白后的 base commit。
 */
function assertCommitStreamInput(input: CreateCommitFilesToBranchRequestStreamInput): string {
  if (input.repositoryId <= 0n) {
    throw new Error('文件提交缺少 repositoryId，无法提交');
  }
  if (input.branchId <= 0n) {
    throw new Error('文件提交缺少 branchId，无法提交');
  }

  const baseCommitId = input.baseCommitId.trim();
  if (!baseCommitId) {
    throw new Error('文件提交缺少 baseCommitId，无法提交');
  }

  if (input.files.length <= 0) {
    throw new Error('文件提交缺少文件变更，无法提交');
  }

  return baseCommitId;
}

/**
 * 创建单个文件的 change 帧。
 *
 * @param file 文件提交载荷。
 * @returns change 请求帧。
 */
function createChangeRequest(file: CommitFilesToBranchFilePayload): CommitFilesToBranchRequest {
  return create(CommitFilesToBranchRequestSchema, {
    payload: {
      case: 'change',
      value: create(CommitBranchChangeSchema, {
        changeType: file.changeType,
        kind: RepositoryEntryKind.FILE,
        path: file.path,
        fileUuid: file.fileUuid,
        oldPath: '',
        contentSize: BigInt(file.bytes.byteLength),
      }),
    },
  });
}

/**
 * 创建单个文件 chunk 帧。
 *
 * @param chunk 当前文件内容分片。
 * @returns chunk 请求帧。
 */
function createChunkRequest(chunk: Uint8Array): CommitFilesToBranchRequest {
  return create(CommitFilesToBranchRequestSchema, {
    payload: {
      case: 'chunk',
      value: chunk,
    },
  });
}

/**
 * 创建 `CommitFilesToBranch` 客户端流。
 *
 * 设计说明：该函数只表达版本提交协议顺序，不理解零件、装配或离线包业务。
 * 业务 service 必须先把自身语义转换成文件载荷，再调用这里统一生成提交流。
 *
 * @param input 提交流输入。
 * @returns 符合后端协议顺序的异步请求流。
 */
export async function* createCommitFilesToBranchRequestStream(
  input: CreateCommitFilesToBranchRequestStreamInput,
): AsyncIterable<CommitFilesToBranchRequest> {
  assertCommitFilesToBranchSignalActive(input.signal);
  const baseCommitId = assertCommitStreamInput(input);
  const chunkSize = input.chunkSize ?? DEFAULT_COMMIT_CHUNK_SIZE;
  logCommitFilesToBranchSummary(input, baseCommitId);

  assertCommitFilesToBranchSignalActive(input.signal);
  yield create(CommitFilesToBranchRequestSchema, {
    payload: {
      case: 'metadata',
      value: create(CommitFilesToBranchMetadataSchema, {
        repositoryId: input.repositoryId,
        branchId: input.branchId,
        message: input.message,
        baseCommitId,
      }),
    },
  });

  for (const file of input.files) {
    // 后端以最近一次 change 作为后续 chunk 的所属文件，因此每个文件必须严格先发 change。
    assertCommitFilesToBranchSignalActive(input.signal);
    yield createChangeRequest(file);

    for (const chunk of createByteChunks(file.bytes, chunkSize)) {
      assertCommitFilesToBranchSignalActive(input.signal);
      yield createChunkRequest(chunk);
    }
  }
}
